import React, { useState } from "react";
import { useFormikContext } from "formik";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { usePopupState, bindTrigger, bindMenu } from "material-ui-popup-state/hooks";
import DateRangeFilterModal from "./DateRangeFilterModal";
import WarehouseFilterModal from "./WarehouseFilterModal";
import CostCenterFilterModal from "./CostCenterFilterModal";
import AssetFieldFilterModal from "./AssetFieldFilterModal";

export default function AddOptionalFiltersButton({ project, reportType, hideDateRangeFilter }) {
  const { values, setFieldValue } = useFormikContext();
  const popupState = usePopupState({ variant: "popover", popupId: "add-optional-filters-popup" });
  const [showDateRangeModal, setShowDateRangeModal] = useState(false);
  const [showWarehouseModal, setShowWarehouseModal] = useState(false);
  const [showCostCenterModal, setShowCostCenterModal] = useState(false);
  const [showAssetFieldModal, setShowAssetFieldModal] = useState(false);

  const warehouses = project?.warehouses || [];
  const costCenters = project?.costCenters || [];
  const displayDateRangeFilter = reportType !== "inventory_requests" && !hideDateRangeFilter;
  const displayWarehouseFilter = warehouses.length > 1;
  const displayCostCenterFilter = costCenters.length > 1;
  const displayAssetFieldFilter = true;

  // Only show the button if at least one filter option is available
  const hasAvailableFilters =
    displayDateRangeFilter || displayWarehouseFilter || displayCostCenterFilter || displayAssetFieldFilter;

  if (!hasAvailableFilters) {
    return null;
  }

  const handleAddDateRangeFilter = () => {
    setShowDateRangeModal(true);
    popupState.close();
  };

  const handleCloseDateRangeModal = () => {
    setShowDateRangeModal(false);
  };

  const handleAddWarehouseFilter = () => {
    setShowWarehouseModal(true);
    popupState.close();
  };

  const handleCloseWarehouseModal = () => {
    setShowWarehouseModal(false);
  };

  const handleAddCostCenterFilter = () => {
    setShowCostCenterModal(true);
    popupState.close();
  };

  const handleCloseCostCenterModal = () => {
    setShowCostCenterModal(false);
  };

  const handleAddAssetFieldFilter = () => {
    setShowAssetFieldModal(true);
    popupState.close();
  };

  const handleCloseAssetFieldModal = () => {
    setShowAssetFieldModal(false);
  };

  const handleSaveDateRange = dateRange => {
    setFieldValue("options", {
      ...values.options,
      date_range: dateRange,
    });
  };

  const handleSaveWarehouse = warehouseIds => {
    setFieldValue("options", {
      ...values.options,
      warehouse_id: warehouseIds,
    });
  };

  const handleSaveCostCenter = costCenterIds => {
    setFieldValue("options", {
      ...values.options,
      cost_center_id: costCenterIds,
    });
  };

  const handleSaveAssetFieldFilters = assetFieldFilters => {
    setFieldValue("options", {
      ...values.options,
      asset_field_filters: assetFieldFilters,
    });
  };

  return (
    <Box mb={1.5}>
      <Button
        variant="outlined"
        color="primary"
        endIcon={<KeyboardArrowDownIcon />}
        {...bindTrigger(popupState)}
      >
        Add Optional Filters
      </Button>
      <Menu
        {...bindMenu(popupState)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{ mt: 0.5 }}
      >
        {displayDateRangeFilter && (
          <MenuItem onClick={handleAddDateRangeFilter}>Add Date Range Filter</MenuItem>
        )}
        {displayWarehouseFilter && (
          <MenuItem onClick={handleAddWarehouseFilter}>Add Warehouse Filter</MenuItem>
        )}
        {displayCostCenterFilter && (
          <MenuItem onClick={handleAddCostCenterFilter}>Add Cost Center Filter</MenuItem>
        )}
        {displayAssetFieldFilter && (
          <MenuItem onClick={handleAddAssetFieldFilter}>Add Asset Field Filters</MenuItem>
        )}
      </Menu>
      {showDateRangeModal && (
        <DateRangeFilterModal
          value={values.options?.date_range}
          title="Initial Date Range"
          description="Future reports will automatically advance by 1 week from this range"
          onClose={handleCloseDateRangeModal}
          onSave={handleSaveDateRange}
        />
      )}
      {showWarehouseModal && (
        <WarehouseFilterModal
          value={values.options?.warehouse_id}
          warehouses={warehouses}
          onSave={handleSaveWarehouse}
          onClose={handleCloseWarehouseModal}
        />
      )}
      {showCostCenterModal && (
        <CostCenterFilterModal
          value={values.options?.cost_center_id}
          costCenters={costCenters}
          onSave={handleSaveCostCenter}
          onClose={handleCloseCostCenterModal}
        />
      )}
      {showAssetFieldModal && (
        <AssetFieldFilterModal
          value={values.options?.asset_field_filters || []}
          onSave={handleSaveAssetFieldFilters}
          onClose={handleCloseAssetFieldModal}
        />
      )}
    </Box>
  );
}
